import { sessionUserAtom } from "@features/Session";
import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { ScrollView } from "react-native";
import { useFragment } from "react-relay";

import { useRequestSessionRefresh } from "@/navigators/SessionChecker/SessionRefreshContext";
import { CheckBox } from "@whoosh/design-system/src/components/controls";
import { Button } from "@whoosh/design-system/src/components/controls";
import { SelectableTile } from "@whoosh/design-system/src/components/controls/SelectableTile";
import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner/InfoBanner";
import { AlertIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  VStack,
  Text,
  Box,
} from "@whoosh/design-system/src/components/primitive";

import { openExternalLink } from "../../../../../../../../../utils/openExternalLink";
import { ProceedToVerifyCodeStepAtom } from "../../../../atoms/actions";
import { VerificationModalCompleteActionAtom } from "../../../../atoms/stores";
import { useCreateEmailVerification } from "../../../../hooks/useCreateEmailVerification";
import { useCreatePhoneVerification } from "../../../../hooks/useCreatePhoneVerification";
import {
  useJoinAdditionalClub,
  type JoinAdditionalClubErrors,
} from "../../../../hooks/useJoinAdditionalClub";
import {
  WHOOSH_PRIVACY_URL,
  WHOOSH_TERMS_URL,
} from "../AccountCreationStep/legalDisclaimerContent";
import { JoinNewClubStepFragment_session$key } from "./__generated__/JoinNewClubStepFragment_session.graphql";

export const JoinNewClubStep = ({
  sessionRef,
}: {
  sessionRef: JoinNewClubStepFragment_session$key | null;
}) => {
  const session = useFragment(JoinNewClubStepFragmentString, sessionRef);
  const sessionUser = useAtomValue(sessionUserAtom);
  const club = session?.currentClientProfile?.member?.club;
  const { clubPolicyLink } = club ?? {};
  const clubPoliciesOwner = club?.name ?? t`Club`;
  const hasClubPolicies = !!clubPolicyLink;
  const [agreeToClubPolicies, setAgreeToClubPolicies] = useState(false);
  const [agreeToWhooshPolicies, setAgreeToWhooshPolicies] = useState(false);
  const [hasJoinError, setHasJoinError] = useState(false);
  const [hasSessionRefreshError, setHasSessionRefreshError] = useState(false);
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);
  const completeAction = useAtomValue(VerificationModalCompleteActionAtom);
  const requestSessionRefresh = useRequestSessionRefresh();
  const proceedToVerifyCode = useSetAtom(ProceedToVerifyCodeStepAtom);
  const [handleJoinClub, isJoiningClub] = useJoinAdditionalClub();
  const [createEmailVerification, isCreatingEmailVerification] =
    useCreateEmailVerification();
  const [createPhoneVerification, isCreatingPhoneVerification] =
    useCreatePhoneVerification();

  const handleJoinError = (errors: JoinAdditionalClubErrors) => {
    const email = session?.user?.email ?? sessionUser?.email;
    const memberUser =
      session?.user?.__typename === "MemberUser" ? session.user : null;
    const phoneNumber = memberUser?.phoneNumber
      ? `${memberUser.phoneNumber.countryCode}${memberUser.phoneNumber.nationalNumber}`
      : null;

    if (email && errors.some((error) => error?.code === matchingEmailError)) {
      createEmailVerification(email, () => {
        proceedToVerifyCode({
          email,
          phone: null,
          type: "ExistingAccount",
        });
      });
    } else if (
      phoneNumber &&
      errors.some((error) => error?.code === matchingPhoneError)
    ) {
      createPhoneVerification(phoneNumber, () => {
        proceedToVerifyCode({
          email: null,
          phone: phoneNumber,
          type: "ExistingAccount",
        });
      });
    } else {
      setHasJoinError(true);
    }
  };

  const handleClickJoin = () => {
    if (isRefreshingSession) {
      return;
    }

    const hasAcceptedRequiredPolicies = hasClubPolicies
      ? agreeToClubPolicies && agreeToWhooshPolicies
      : agreeToWhooshPolicies;

    if (hasAcceptedRequiredPolicies) {
      setHasJoinError(false);

      const refreshSessionAndComplete = () => {
        setHasSessionRefreshError(false);
        setIsRefreshingSession(true);
        requestSessionRefresh({
          onCompleted: () => {
            setIsRefreshingSession(false);
            completeAction?.();
          },
          onError: () => {
            setIsRefreshingSession(false);
            setHasSessionRefreshError(true);
          },
        });
      };

      if (hasSessionRefreshError) {
        refreshSessionAndComplete();
        return;
      }

      handleJoinClub(
        {
          acceptedLiabilityWaiver: hasClubPolicies ? agreeToClubPolicies : true,
        },
        refreshSessionAndComplete,
        handleJoinError
      );
    }
  };
  return (
    <Box flex={1} paddingHorizontal={6}>
      <ScrollView
        contentContainerStyle={{ flexGrow: 1, width: "100%" }}
        showsVerticalScrollIndicator={false}
      >
        <VStack flexGrow={1} width="100%">
          <VStack mb={8}>
            <Text variant={"mainTitle2"}>{t`Welcome to ${
              club?.name ?? ""
            }!`}</Text>
          </VStack>
          {hasJoinError ? (
            <Box marginBottom={4}>
              <InfoBanner
                colorTheme="ERROR"
                leadingIcon={AlertIcon}
                message={t`We couldn't connect this account to the club. Please contact the club for help.`}
                testID="join-new-club-error"
              />
            </Box>
          ) : null}
          {hasSessionRefreshError ? (
            <Box marginBottom={4}>
              <InfoBanner
                colorTheme="ERROR"
                leadingIcon={AlertIcon}
                message={t`Something went wrong. Please try again.`}
                testID="session-refresh-error"
              />
            </Box>
          ) : null}
          <VStack style={{ marginBottom: 120 }}>
            <VStack gap={3} flexGrow={1} width="100%">
              {hasClubPolicies ? (
                <SelectableTile
                  isSelected={agreeToClubPolicies}
                  setSelected={(isSelected) => {
                    setAgreeToClubPolicies(isSelected);
                  }}
                  leadingElement={(isSelected) => (
                    <CheckBox
                      ariaLabel={t`Agree to club policies`}
                      testID="join-new-club-policy-checkbox"
                      isChecked={isSelected}
                    />
                  )}
                  title={t`Agree to club policies`}
                />
              ) : null}
              <SelectableTile
                isSelected={agreeToWhooshPolicies}
                setSelected={(isSelected) => {
                  setAgreeToWhooshPolicies(isSelected);
                }}
                leadingElement={(isSelected) => (
                  <CheckBox
                    ariaLabel={t`Agree to Whoosh policies`}
                    testID="join-new-club-whoosh-checkbox"
                    isChecked={isSelected}
                  />
                )}
                titleNumberOfLines={2}
                title={t`Agree to Whoosh terms and privacy policy`}
              />
              <Text variant="mainTiny" color="text-secondary">
                {t`By clicking Join Club you agree to the` + " "}
                {hasClubPolicies ? (
                  <Text
                    variant="mainTiny"
                    accessibilityRole="link"
                    onPress={() => {
                      openExternalLink(clubPolicyLink ?? "");
                    }}
                  >
                    {t`${clubPoliciesOwner} Policies`}{" "}
                  </Text>
                ) : null}
                {hasClubPolicies ? t`and the Whoosh` + " " : t`Whoosh` + " "}
                <Text
                  variant="mainTiny"
                  accessibilityRole="link"
                  onPress={() => {
                    openExternalLink(WHOOSH_TERMS_URL);
                  }}
                >
                  {t`Terms & Conditions` + " "}
                </Text>
                {t`and` + " "}
                <Text
                  variant="mainTiny"
                  accessibilityRole="link"
                  onPress={() => {
                    openExternalLink(WHOOSH_PRIVACY_URL);
                  }}
                >
                  {t`Privacy Policy` + ". "}
                </Text>
                {t`You may receive SMS and email notifications with updates on your booking from us but can opt out anytime.`}
              </Text>
            </VStack>
          </VStack>
        </VStack>
      </ScrollView>
      <VStack gap={6}>
        <Button
          isDisabled={
            hasClubPolicies
              ? !agreeToClubPolicies || !agreeToWhooshPolicies
              : !agreeToWhooshPolicies
          }
          onPress={handleClickJoin}
          isLoading={
            isJoiningClub ||
            isRefreshingSession ||
            isCreatingEmailVerification ||
            isCreatingPhoneVerification
          }
          accessibilityLabel={
            hasSessionRefreshError ? t`Try Again button` : t`Join Club button`
          }
        >
          {hasSessionRefreshError ? t`Try Again` : t`Join Club`}
        </Button>
      </VStack>
    </Box>
  );
};

const matchingEmailError = "matching_email_not_verified";
const matchingPhoneError = "matching_phone_not_verified";

export const JoinNewClubStepFragmentString = graphql`
  fragment JoinNewClubStepFragment_session on Session {
    user {
      __typename
      email
      ... on MemberUser {
        phoneNumber {
          countryCode
          nationalNumber
        }
      }
    }
    currentClientProfile {
      member {
        club {
          name
          clubPolicyLink
        }
      }
    }
  }
`;
